import type { ParentChildGroup } from '@repo/types/parent';

import { useGetChildrenGroups } from '@repo/api/parent/useGetChildrenGroups';
import { useGetMyChildren } from '@repo/api/parent/useGetMyChildren';
import { Link } from 'react-router';

import { ChildAvatar } from '@/components/features/parent/ChildAvatar';
import { ChildSectionList } from '@/components/features/parent/ChildSectionList';
import ParentPageHeader from '@/components/features/parent/layout/ParentPageHeader';
import { Badge } from '@/components/ui/badge';

export default function ParentGroupsPage() {
  const { data: children = [], isPending: isPendingChildren, isError, errorMessage } = useGetMyChildren();

  const { results, failed, isPending } = useGetChildrenGroups(children, !isPendingChildren);

  return (
    <div className={'base-direction-col'}>
      <div className={'flex w-full grow flex-col gap-6'}>
        <ParentPageHeader title={'Grupy'} />

        <ChildSectionList
          results={results}
          failed={failed}
          isPending={isPendingChildren || isPending}
          isError={isError}
          errorMessage={errorMessage}
          collapsible={false}
          isEmpty={(groups) => !groups.length}
          emptyMessage={'Dziecko nie należy do żadnej grupy'}
          summary={(groups) => `Grup: ${groups.length}`}
        >
          {(groups) => (
            <ul className={'flex flex-col gap-2'}>
              {groups.map((group) => (
                <GroupCard key={group.id} group={group} />
              ))}
            </ul>
          )}
        </ChildSectionList>
      </div>
    </div>
  );
}

function GroupCard({ group }: { group: ParentChildGroup }) {
  return (
    <li className={'border-border-primary bg-card/75 rounded-md border'}>
      <Link to={`/parent/groups/${group.id}`} className={'outline-focus-ring hover:bg-accent/40 flex flex-col gap-2 rounded-md p-3 outline-0 transition-colors focus-visible:outline-3'}>
        <div className={'flex flex-col'}>
          <span className={'text-foreground text-sm font-medium'}>{group.name}</span>
          {group.desc ? <span className={'text-muted-foreground text-xs'}>{group.desc}</span> : null}
        </div>

        {group.users.length ? (
          <div className={'flex flex-wrap items-center gap-2'}>
            <span className={'text-muted-foreground text-xs'}>Nauczyciele:</span>
            {group.users.map((teacher) => (
              <Badge key={teacher.id} variant={'muted'} size={'sm'}>
                <ChildAvatar child={teacher} className={'size-4'} />
                {teacher.name} {teacher.surname}
              </Badge>
            ))}
          </div>
        ) : null}
      </Link>
    </li>
  );
}
